import React, { useCallback, useEffect, useState } from "react";
import { Card, Grid, Image, Header, Button, Icon, Divider, Modal } from "semantic-ui-react";
import PackageSelector, { PackageItem } from "./PackageSelector";
import CONFIG from "config/config";
import MeHealthHistoryModal from "./MeHealthHistoryModal";
import "react-lib/apps/MEH/CardBuyBillingPackage.scss";
import { formatISOToThaiDate } from "react-lib/utils/dateUtils";
import SunEditor from "suneditor-react";
import SnackMessage from "../common/SnackMessage";

type CardBuyBillingPackageProps = {
  BillingAndPackageSequence: any;
  BillingCreditWallet: any;
  BillingCarts: any;
  onHistoryClick?: () => void;
  controller: any;
  runSequence: any;
  onEvent: any;
  setProp: any;
  languageUX: "en" | "th";
  onProceed?: () => void;
  termsAndConditionsList?: any;
  errorMessage?: any;
};

const HOSPITAL_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/hospital.png`;
const TITLE = "MeHealth Credit";
const BALANCE_LABEL = "ยอดคงเหลือ";
const HISTORY_LABEL = "ประวัติการทำรายการ";

const ICON = {
  IconCredit: "/static/images/icon_credit.svg",
};

const TERMS_KEY = "BillingPackageTermsAccepted";

export const formatAddress = (divisionProfile: any) => {
  const addressText = divisionProfile?.address_text?.trim() || "";
  const cityText = divisionProfile?.city?.name?.trim() || "";
  const districtText = divisionProfile?.district?.name?.trim() || "";
  const provinceText = divisionProfile?.province?.name?.trim() || "";
  const zipCodeText = divisionProfile?.city?.zipcode?.trim() || "";

  return [addressText, cityText, districtText, provinceText, zipCodeText]
  .filter(Boolean)
  .join(" ");
};

const CardBuyBillingPackage = (props: CardBuyBillingPackageProps) => {
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  // const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const creditBalance = props.BillingCreditWallet?.balance ?? 0;
  const creditUpdatedAt = props.BillingCreditWallet?.balance_updated_at;

  // const handleOnScroll = () => {
  //   const editorElement = document.getElementsByClassName("sun-editor-editable")[0];

  //   if (editorElement) {
  //     const scrollTop = editorElement.scrollTop;
  //     const scrollHeight = editorElement.scrollHeight;
  //     const clientHeight = editorElement.clientHeight;

  //     const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

  //     if (scrolledToBottom || scrollHeight <= clientHeight) {
  //       setHasScrolledToBottom(true);
  //     }
  //   }
  // };

  // const handleOnLoad = () => {
  //   const editorElement = document.getElementsByClassName("sun-editor-editable")[0];
  //   if (editorElement) {
  //     const scrollHeight = editorElement.scrollHeight;
  //     const clientHeight = editorElement.clientHeight;

  //     if (scrollHeight <= clientHeight) {
  //       console.log(scrollHeight, clientHeight);
  //       setHasScrolledToBottom(true);
  //     }
  //   }
  // };

  useEffect(() => {
    // TODO: need check runsequnce
    props.runSequence({ sequence: "BillingAndPackage", restart: true });
    return () => {
      // props.runSequence({ sequence: "BillingAndPackage", clear: true });
    };
  }, [props.controller.division]);

  useEffect(() => {
    props.onEvent({
      message: "HandleGetTermsAndConditionsList",
    });
  }, []);

  useEffect(() => {
    if (props.termsAndConditionsList?.length) {
      const publishedTerms = props.termsAndConditionsList.filter(
        (t: any) => t.status === "PUBLISHED"
      );
      const latestPublished = publishedTerms.sort(
        (a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )[0];

      if (latestPublished?.content) {
        setEditorContent(latestPublished.content);
      } else {
        setEditorContent("");
      }
    } else {
      setEditorContent("");
    }
  }, [props.termsAndConditionsList]);

  useEffect(() => {
    const accepted = localStorage.getItem(TERMS_KEY) === "true";
    setHasAcceptedTerms(accepted);
  }, []);

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.BillingAndPackage`, null);
  };

  return (
    <div id="CardBuyBillingPackage" style={{ padding: "6px" }}>
      {/* --- ErrorMessage & SuccessMessage --- */}
      <SnackMessage
        error={props.errorMessage?.BillingAndPackage}
        success={null}
        onClose={handleCloseSnackMessage}
      />

      <Card fluid style={{ borderRadius: 14 }}>
        <Card.Content>
          <Grid stackable verticalAlign="middle" columns={2} divided>
            {/* Left: clinic block */}
            <Grid.Column width={6}>
              <div style={{ display: "flex", gap: 14 }}>
                <Image
                  circular
                  src={
                    props.BillingAndPackageSequence?.divisionProfile?.image_file ||
                    HOSPITAL_IMAGE_URL
                  }
                  alt={props.BillingAndPackageSequence?.divisionProfile?.division_name}
                  rounded
                  style={{ width: 92, height: 92, objectFit: "cover", flexShrink: 0 }}
                />
                <div style={{ lineHeight: 1.4 }}>
                  <Header as="h4" style={{ marginBottom: 6 }}>
                    {props.BillingAndPackageSequence?.divisionProfile?.division_name}
                  </Header>
                  <div style={{ color: "rgba(0,0,0,.6)", whiteSpace: "pre-wrap" }}>
                    <b>สถานที่ตั้ง :</b>{" "}
                    {formatAddress(props.BillingAndPackageSequence?.divisionProfile)}
                  </div>

                  <div style={{ color: "rgba(0,0,0,.6)" }}>
                    <b>เลขที่ประจำตัวผู้เสียภาษี :</b>{" "}
                    {props.BillingAndPackageSequence?.divisionProfile?.tax_payer_id ?? ""}
                  </div>
                </div>
              </div>
            </Grid.Column>

            {/* Right: credit block */}
            <Grid.Column width={10}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 2fr auto",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <Header as="h4" style={{ color: "#e91e63", margin: 0 }}>
                  {TITLE}
                </Header>
                <div />
                <div />
                <div>
                  <div style={{ fontWeight: 700 }}>{BALANCE_LABEL}</div>
                  {creditUpdatedAt && (
                    <div style={{ color: "rgba(0,0,0,.6)" }}>
                      {formatISOToThaiDate(creditUpdatedAt)}
                    </div>
                  )}
                </div>
                <Header as="h2" style={{ margin: 0 }}>
                  <Image
                    src={ICON.IconCredit}
                    alt="credit"
                    style={{ width: 30, height: 30, marginTop: 0, marginRight: 5 }}
                  />
                  {Number(creditBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}{" "}
                  Credit
                </Header>
                <div style={{ justifySelf: "end" }}>
                  <Button color="pink" size="small" onClick={() => setOpenHistoryModal(true)}>
                    <Icon name="history" />
                    {HISTORY_LABEL}
                  </Button>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>
      <div style={{ marginTop: 16 }}>
        <PackageSelector
          languageUX={props.languageUX}
          runSequence={props.runSequence}
          meHealthPackageList={props.BillingAndPackageSequence?.meHealthPackageList}
          BillingCarts={props.BillingCarts}
          onProceed={props.onProceed}
          creditIcon={ICON.IconCredit}
          hasAcceptedTerms={hasAcceptedTerms}
          setShowTermsPopup={setShowTermsPopup}
        />
      </div>
      <MeHealthHistoryModal
        open={openHistoryModal}
        onClose={() => setOpenHistoryModal(false)}
        BillingAndPackageSequence={props.BillingAndPackageSequence}
        BillingCreditWallet={props.BillingCreditWallet}
        runSequence={props.runSequence}
        setProp={props.setProp}
      />
      <Modal
        open={showTermsPopup}
        onClose={() => {
          setShowTermsPopup(false);
          // setHasScrolledToBottom(false);
        }}
        size="large"
      >
        <Modal.Content>
          <h2 style={{ color: "#F382A0", marginTop: "20px" }}>
            นโยบายการให้บริการ และเงื่อนไขในการซื้อแพ็กเกจ (Package Terms & Conditions)
          </h2>
          <SunEditor
            disable={true}
            // onScroll={handleOnScroll}
            // onLoad={handleOnLoad}
            setContents={editorContent}
            setOptions={{
              buttonList: [],
              maxHeight: "500px",
              height: "100%",
              resizingBar: false,
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 5, marginTop: 20 }}>
            <Button
              // disabled={!hasScrolledToBottom}
              style={{
                backgroundColor: "#F382A0",
                color: "#fff",
                // opacity: hasScrolledToBottom ? 1 : 0.5,
                borderRadius: 999,
              }}
              onClick={() => {
                setHasAcceptedTerms(true);
                localStorage.setItem(TERMS_KEY, "true");
                setShowTermsPopup(false);

                props.onEvent({
                  message: "HandleAcceptedTermsAndConditions",
                });
              }}
            >
              ยินยอม
            </Button>
            <Button
              style={{ borderRadius: 999 }}
              onClick={() => {
                setShowTermsPopup(false);
                // setHasScrolledToBottom(false);
              }}
            >
              ปิดหน้าต่าง
            </Button>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default CardBuyBillingPackage;
